import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { registerVendor } from '../../redux/slices/vendorSlice';
import { toast } from 'sonner';
import { Store, Building, CreditCard, MapPin } from 'lucide-react';

const VendorRegistration = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const { loading, error } = useSelector((state) => state.vendor);

  const [formData, setFormData] = useState({
    storeName: '',
    storeDescription: '',
    businessName: '',
    gstNumber: '',
    panNumber: '',
    contactEmail: user?.email || '',
    contactPhone: '',
    businessAddress: {
      address: '',
      city: '',
      state: '',
      postalCode: '',
      country: 'India',
    },
    bankDetails: {
      accountHolderName: '',
      accountNumber: '',
      ifscCode: '',
      bankName: '',
      branchName: '',
      upiId: '',
    },
  });

  const handleChange = (e, section = null) => {
    const { name, value } = e.target;

    if (section) {
      setFormData((prev) => ({
        ...prev,
        [section]: {
          ...prev[section],
          [name]: value,
        },
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.storeName || !formData.businessName || !formData.contactPhone) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Validate address
    if (
      !formData.businessAddress.address ||
      !formData.businessAddress.city ||
      !formData.businessAddress.state ||
      !formData.businessAddress.postalCode
    ) {
      toast.error('Please complete business address');
      return;
    }

    try {
      await dispatch(registerVendor(formData)).unwrap();
      toast.success('Vendor application submitted successfully!');
      navigate('/vendor/application-status');
    } catch (err) {
      toast.error(err.message || 'Failed to submit application');
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Become a Vendor</h1>
        <p className="text-gray-600">
          Join our marketplace and start selling your products to thousands of customers
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Store Information */}
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center gap-2 mb-4">
            <Store className="w-5 h-5" />
            <h2 className="text-xl font-semibold">Store Information</h2>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                Store Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="storeName"
                placeholder="e.g., Fashion Hub Store"
                value={formData.storeName}
                onChange={handleChange}
                className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                This will be your public store name
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Store Description
              </label>
              <textarea
                name="storeDescription"
                placeholder="Tell customers about your store and products..."
                value={formData.storeDescription}
                onChange={handleChange}
                className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
                rows={4}
              />
            </div>
          </div>
        </div>

        {/* Business Details */}
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center gap-2 mb-4">
            <Building className="w-5 h-5" />
            <h2 className="text-xl font-semibold">Business Details</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-2">
                Business Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="businessName"
                placeholder="Legal business name"
                value={formData.businessName}
                onChange={handleChange}
                className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">GST Number</label>
              <input
                type="text"
                name="gstNumber"
                placeholder="22AAAAA0000A1Z5"
                value={formData.gstNumber}
                onChange={handleChange}
                className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
              />
              <p className="text-xs text-gray-500 mt-1">Optional if not registered</p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">PAN Number</label>
              <input
                type="text"
                name="panNumber"
                placeholder="ABCDE1234F"
                value={formData.panNumber}
                onChange={handleChange}
                className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Contact Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                name="contactEmail"
                placeholder="business@example.com"
                value={formData.contactEmail}
                onChange={handleChange}
                className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Contact Phone <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                name="contactPhone"
                placeholder="+91 9876543210"
                value={formData.contactPhone}
                onChange={handleChange}
                className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
                required
              />
            </div>
          </div>
        </div>

        {/* Business Address */}
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center gap-2 mb-4">
            <MapPin className="w-5 h-5" />
            <h2 className="text-xl font-semibold">Business Address</h2>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                Address <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="address"
                placeholder="Street address"
                value={formData.businessAddress.address}
                onChange={(e) => handleChange(e, 'businessAddress')}
                className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  City <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="city"
                  placeholder="City"
                  value={formData.businessAddress.city}
                  onChange={(e) => handleChange(e, 'businessAddress')}
                  className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  State <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="state"
                  placeholder="State"
                  value={formData.businessAddress.state}
                  onChange={(e) => handleChange(e, 'businessAddress')}
                  className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Postal Code <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="postalCode"
                  placeholder="PIN Code"
                  value={formData.businessAddress.postalCode}
                  onChange={(e) => handleChange(e, 'businessAddress')}
                  className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Country</label>
                <input
                  type="text"
                  name="country"
                  value={formData.businessAddress.country}
                  onChange={(e) => handleChange(e, 'businessAddress')}
                  className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black bg-gray-50"
                  readOnly
                />
              </div>
            </div>
          </div>
        </div>

        {/* Bank Details */}
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center gap-2 mb-4">
            <CreditCard className="w-5 h-5" />
            <h2 className="text-xl font-semibold">Bank Details (for Payouts)</h2>
          </div>

          <p className="text-sm text-gray-600 mb-4">
            You can add or update these details later from your profile
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                Account Holder Name
              </label>
              <input
                type="text"
                name="accountHolderName"
                placeholder="As per bank records"
                value={formData.bankDetails.accountHolderName}
                onChange={(e) => handleChange(e, 'bankDetails')}
                className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Account Number
              </label>
              <input
                type="text"
                name="accountNumber"
                placeholder="Bank account number"
                value={formData.bankDetails.accountNumber}
                onChange={(e) => handleChange(e, 'bankDetails')}
                className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">IFSC Code</label>
              <input
                type="text"
                name="ifscCode"
                placeholder="ABCD0123456"
                value={formData.bankDetails.ifscCode}
                onChange={(e) => handleChange(e, 'bankDetails')}
                className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Bank Name</label>
              <input
                type="text"
                name="bankName"
                placeholder="e.g., HDFC Bank"
                value={formData.bankDetails.bankName}
                onChange={(e) => handleChange(e, 'bankDetails')}
                className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Branch Name</label>
              <input
                type="text"
                name="branchName"
                placeholder="Branch location"
                value={formData.bankDetails.branchName}
                onChange={(e) => handleChange(e, 'bankDetails')}
                className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                UPI ID (Optional)
              </label>
              <input
                type="text"
                name="upiId"
                placeholder="yourname@upi"
                value={formData.bankDetails.upiId}
                onChange={(e) => handleChange(e, 'bankDetails')}
                className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
              />
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex gap-4">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="flex-1 px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="flex-1 bg-black text-white py-3 rounded-lg font-semibold hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Submitting...' : 'Submit Application'}
          </button>
        </div>

        <p className="text-sm text-gray-600 text-center">
          By submitting, you agree to our vendor terms and conditions
        </p>
      </form>
    </div>
  );
};

export default VendorRegistration;
